import { useDecision } from '@optimizely/react-sdk'
import { useTranslation } from 'next-i18next'
import { type FC, Fragment, useContext } from 'react'

import { ToggleFilter as DeprecatedToggleFilter } from '@/components/shared/deprecated/toggle-filter/ToggleFilter'
import { FILTER_CANONICAL } from '@/constants/search'
import { SearchContext } from '@/contexts/SearchContext'
import { getLocaleLabelKeyOverride } from '@/domain/filters'
import { getParentFilter } from '@/domain/srp/filters/getParentFilter'
import { type FilterGroup as FilterGroupType, FilterControlType } from '@/generated'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { type SearchActionsResult } from '@/hooks/srp/useSearchActions'
import { FEATURE_FLAG } from '@/lib/optimizely'
import {
  isAttributeFilter,
  isCategoryTree,
  isDateFilter,
  isLocationTree,
  isRangeFilterWithMinMax,
} from '@/types/search'
import { isAnyJobsCategory } from '@/utils/category/categoryTree'

import { DateFilter } from '../filter-types/DateFilter'
import { DateRangeFilter } from '../filter-types/DateRangeFilter'
import { IntegerRangeFilter } from '../filter-types/IntegerRangeFilter'
import { MultiSelectFilter } from '../filter-types/MultiSelectFilter'
import { RadioFilter } from '../filter-types/RadioFilter'
import { ToggleFilter } from '../filter-types/ToggleFilter'
import { FiltersDivider } from '../styled'
import { type AttributeFilterProps } from './FiltersAccordion'

const getComponentFromType = (controlType: string) => {
  switch (controlType) {
    case FilterControlType.Date:
      return DateFilter
    case FilterControlType.DateRange:
      return DateRangeFilter
    default:
      return null
  }
}

export type FilterGroupProps = {
  /**
   * List of every filter group.
   * Used to get the parent filter for a given filter.
   */
  filterGroups: FilterGroupType[]
  /**
   * Filter group being rendered currently.
   */
  filterGroup: FilterGroupType
  isMobile?: boolean
} & Pick<SearchActionsResult, 'attributeAppliedFilters' | 'rangeAppliedFilters' | 'refetchResults'>

export const FilterGroup: FC<FilterGroupProps> = ({
  filterGroups,
  filterGroup,
  isMobile,
  refetchResults,
  attributeAppliedFilters,
  rangeAppliedFilters,
}) => {
  const { t } = useTranslation(['srp'])
  const { data } = useGetSearchResultsData()
  const { searchQuery } = data || {}
  const selectedCategoryId = searchQuery?.category?.id || 0

  const { resetDateFilter, handleDateRangeFiltersChange, handleDateFiltersChange, loadingResults } =
    useContext(SearchContext)
  const [{ enabled: shouldShowNewToggleFilters }] = useDecision(
    FEATURE_FLAG.SUPPORT_NEW_SCHEMA_FOR_TOGGLES_FILTER
  )
  const ToggleFilterComponent = shouldShowNewToggleFilters ? ToggleFilter : DeprecatedToggleFilter

  return (
    <>
      {filterGroup.filters.map((filter, index) => {
        if (isCategoryTree(filter) || isLocationTree(filter)) {
          return null
        }

        // Do not show Salary filter in any of the Jobs categories
        if (isAnyJobsCategory(selectedCategoryId) && filter.name === FILTER_CANONICAL.PRICE_RANGE) {
          return null
        }

        const attributeFilter = isAttributeFilter(filter) ? filter : undefined

        let label = filter.label
        const localeKeyOverride = getLocaleLabelKeyOverride(filter.name, selectedCategoryId)
        if (localeKeyOverride) {
          label = t(`filters.${localeKeyOverride}_range.${localeKeyOverride}`)
        }

        const parentFilter = getParentFilter(filter, filterGroups)
        const key = `${filter.name}-${index}`

        if (attributeFilter) {
          const attributeProps: AttributeFilterProps = {
            appliedFilters: attributeAppliedFilters,
            attributeFilter,
            data,
            isMobile,
            label,
            name: filter.name,
            parentFilter,
            refetchResults,
          }

          return (
            <Fragment key={key}>
              <FiltersDivider />
              {filter.type === FilterControlType.Checkboxes ||
              filter.type === FilterControlType.MultiSelect ? (
                <MultiSelectFilter {...attributeProps} />
              ) : filter.type === FilterControlType.Radio ? (
                <RadioFilter {...attributeProps} />
              ) : filter.type === FilterControlType.Toggles ? (
                <ToggleFilterComponent {...attributeProps} />
              ) : null}
            </Fragment>
          )
        }

        const rangeFilter = isRangeFilterWithMinMax(filter) ? filter : undefined

        if (rangeFilter) {
          if (filter.type === FilterControlType.Range) {
            return (
              <Fragment key={key}>
                <FiltersDivider />

                <IntegerRangeFilter
                  appliedFilters={rangeAppliedFilters}
                  data={data}
                  label={label}
                  name={filter.name}
                  parentFilter={parentFilter}
                  rangeFilter={rangeFilter}
                  refetchResults={refetchResults}
                />
              </Fragment>
            )
          }
        }

        const dateFilter = isDateFilter(filter) ? filter : undefined
        const FilterComponent = getComponentFromType(filter.type)

        return (
          <Fragment key={key}>
            <FiltersDivider />

            {FilterComponent && (
              <FilterComponent
                rangeFilter={rangeFilter}
                label={label}
                name={filter.name}
                parentFilter={parentFilter}
                onDateRangeFilterChange={handleDateRangeFiltersChange}
                resetDateFilter={resetDateFilter}
                dateFilter={dateFilter}
                onDateFilterChange={handleDateFiltersChange}
                isSrpLoading={loadingResults}
              />
            )}
          </Fragment>
        )
      })}
    </>
  )
}
